import { RichText } from "@components/cms/rich-text-renderer/RichText";
import type { RawProductAttribute } from "@graphql/generated/types";
import { CommonCMS } from "@lib/constants/contentful";
import type { ProductDeliveryUsp } from "@lib/contentful/mappers/mapProductContent";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Attributes } from "@lib/enums/ProductAttributes";
import type { ProductMedia } from "@lib/product/types";
import {
    getProductAttributeValue,
    getProductAttributeValueByNames,
} from "@lib/utils/attributesUtils";
import { useStore } from "@store/store";
import { ReadMore } from "@ecco/ui/read-more";
import { Text } from "@ecco/ui/text";
import { ListItem, UnorderedList } from "@ecco/ui/list";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
} from "@ecco/ui/accordion";
import { Box } from "@ecco/ui/box";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { Image } from "@ecco/ui/image";
import { Tooltip } from "@ecco/ui/tooltip";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import { useTooltip } from "@ecco/ui/use-tooltip";
import dynamic from "next/dynamic";
import { memo, type ReactNode, useRef } from "react";
import type { LineItemStoreFinder } from "../right-panel/buy-card/click-and-collect/ClickAndCollectCard";
import { accordionStyles, uspStyles } from "./AccordionSingleProduct.styles";
import { ShippingMethodTypes } from "@features/checkout/types";
import { getCountryForSiteByLocale } from "@config/site/site-config";
import {
    ESTIMATED_DELIVERY_HEADING_VB,
    appendEstimatedDeliveryDate,
    formatEstimatedDeliveryDate,
    isEstimatedDeliveryHeading,
    isEstimatedDeliveryHeadingVB,
    pickEstimatedDeliveryDate,
} from "@lib/utils/pdp/estimatedDelivery";
import { useDeliveryEstimate } from "./hooks/useDeliveryEstimate";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";

const StoreLocatorModal = dynamic(() => import("@components/click-and-collect/StoreLocatorModal"), {
    ssr: false,
});

export interface AccordionSingleProductProps {
    productName: string;
    image: ProductMedia;
    attributesRaw: RawProductAttribute[];
    locale: string;
    uspInfos?: ProductDeliveryUsp[];
    shippingAndReturns?: string | ReactNode;
    skus?: string[];
}

const USPTooltip = ({ label }: { label: string }) => {
    const tooltipRef = useRef(undefined);
    const { isTooltipOpen, setIsTooltipOpen } = useTooltip({ tooltipRef });

    return (
        <Tooltip isOpen={isTooltipOpen} hasArrow={false} label={label} closeOnScroll>
            <Box as="span" sx={uspStyles.tooltipIcon}>
                <ECCOIcon
                    ref={tooltipRef}
                    height={4}
                    name="info"
                    onClick={() => setIsTooltipOpen((prev) => !prev)}
                />
            </Box>
        </Tooltip>
    );
};

const AccordionSingleProduct = memo(
    ({
        productName,
        attributesRaw,
        image,
        locale,
        uspInfos,
        shippingAndReturns,
        skus = [],
    }: AccordionSingleProductProps) => {
        const { get: getMicrocopy, getMultiple } = useMicrocopy();
        const { isOpen, onClose } = useDisclosure();

        const activeProduct = useStore((state) => state.data.pdpState.activeProduct);
        const selectedVariant = activeProduct?.variant;
        const clickAndCollectStores = useStore(
            (state) => state.data.pdpState.clickAndCollectStores
        );
        const hasEstimatedDeliveryDate = useFeatureFlag("hasEstimatedDeliveryDate");

        const { data: deliveryEstimates } = useDeliveryEstimate({
            skus,
            country: getCountryForSiteByLocale(locale),
            enabled: hasEstimatedDeliveryDate,
        });

        const estimatedDeliveryDate = hasEstimatedDeliveryDate
            ? pickEstimatedDeliveryDate(deliveryEstimates, selectedVariant?.sku, "earliest")
            : null;
        const productNumber = getProductAttributeValue(attributesRaw, Attributes.productNumber);
        const fbStory = getProductAttributeValueByNames(
            attributesRaw,
            [Attributes.fbStory],
            locale,
            true
        );
        const featureTexts = [
            Attributes.fbText1,
            Attributes.fbText2,
            Attributes.fbText3,
            Attributes.fbText4,
            Attributes.fbText5,
            Attributes.fbText6,
        ]
            .map((attr) => getProductAttributeValue(attributesRaw, attr, locale))
            .filter(Boolean);
        const descriptiveHeading = getProductAttributeValue(
            attributesRaw,
            Attributes.descriptiveHeading,
            locale
        );

        const selectedProductData: LineItemStoreFinder = {
            name: productName,
            selectedVariant,
            image,
            descriptiveHeading,
        };

        const microcopies = getMultiple(
            CommonCMS.page,
            [
                CommonCMS.PDP.itemNumber,
                CommonCMS.PDP.featuresAndBenefits,
                CommonCMS.PDP.shippingAndReturns,
            ],
            {
                replacements: {
                    storesCount: clickAndCollectStores?.length || 0,
                },
            }
        );

        return (
            <Box w={"100%"} px={[3, 3, 3, 0]} data-testid="accordionSingleProduct">
                {uspInfos?.map(({ id, heading, text, tooltipInfo, icon }) => {
                    if (!text) return null;
                    const isDeliveryHeading =
                        hasEstimatedDeliveryDate && isEstimatedDeliveryHeading(heading);
                    const isDeliveryHeadingVB = isEstimatedDeliveryHeadingVB(heading);

                    if (
                        (isDeliveryHeading || (hasEstimatedDeliveryDate && isDeliveryHeadingVB)) &&
                        !estimatedDeliveryDate
                    )
                        return null;

                    const uspText =
                        isDeliveryHeadingVB && estimatedDeliveryDate
                            ? appendEstimatedDeliveryDate(
                                  text,
                                  formatEstimatedDeliveryDate(estimatedDeliveryDate, locale)
                              )
                            : text;

                    return (
                        <Box
                            key={id}
                            {...uspStyles.container}
                            data-ab-id={
                                isDeliveryHeadingVB ? ESTIMATED_DELIVERY_HEADING_VB : heading.trim()
                            }
                            className={isDeliveryHeadingVB ? "ab-hide" : ""}
                        >
                            {icon && (
                                <Box {...uspStyles.iconWrapper}>
                                    <Image src={icon.src} alt={icon.alt} />
                                </Box>
                            )}

                            <Box sx={uspStyles.textWrapper}>
                                <RichText data={uspText} />
                                {tooltipInfo && <USPTooltip label={tooltipInfo} />}
                            </Box>
                        </Box>
                    );
                })}
                {fbStory && (
                    <ReadMore
                        showMoreLabel={getMicrocopy(CommonCMS.shared, "generic.readMore")}
                        showLessLabel={getMicrocopy(CommonCMS.shared, "generic.readLess")}
                        collapsedHeight={["3rem"]}
                        w={"95%"}
                        text={fbStory}
                        textStyle={accordionStyles.itemDescription}
                    />
                )}
                <Box mt={4} mr={2}>
                    <Accordion allowToggle w="full">
                        {/* Features & Benefits */}
                        {featureTexts.length > 0 && (
                            <AccordionItem {...accordionStyles.accordionItem}>
                                <AccordionButton {...accordionStyles.accordionButtonProps}>
                                    <Text {...accordionStyles.accordionHeaderText}>
                                        {microcopies[CommonCMS.PDP.featuresAndBenefits]}
                                    </Text>
                                    <AccordionIcon
                                        data-testid="arrow-icon"
                                        {...accordionStyles.accordionChevronIconProps}
                                    />
                                </AccordionButton>
                                <AccordionPanel {...{ paddingLeft: 0, paddingBottom: 4, pl: 1 }}>
                                    <UnorderedList>
                                        {featureTexts.map((text, index) => (
                                            <ListItem
                                                key={index}
                                                {...accordionStyles.accordionListItem}
                                            >
                                                {text}
                                            </ListItem>
                                        ))}
                                    </UnorderedList>
                                </AccordionPanel>
                            </AccordionItem>
                        )}
                        {/* Shipping & Returns */}
                        {shippingAndReturns && (
                            <AccordionItem {...accordionStyles.accordionItem}>
                                <AccordionButton {...accordionStyles.accordionButtonProps}>
                                    <Text {...accordionStyles.accordionHeaderText}>
                                        {microcopies[CommonCMS.PDP.shippingAndReturns]}
                                    </Text>
                                    <AccordionIcon
                                        data-testid="arrow-icon"
                                        {...accordionStyles.accordionChevronIconProps}
                                    />
                                </AccordionButton>
                                <AccordionPanel
                                    {...accordionStyles.accordionBenefitsPanelProps}
                                    css={accordionStyles.accordionBenefitsPanelContentCss}
                                >
                                    {<>{shippingAndReturns}</>}
                                </AccordionPanel>
                            </AccordionItem>
                        )}
                    </Accordion>
                    <Text {...accordionStyles.itemId}>
                        {microcopies[CommonCMS.PDP.itemNumber]} {productNumber}
                    </Text>
                </Box>
                {isOpen && (
                    <StoreLocatorModal
                        isOpen={isOpen}
                        onClose={onClose}
                        selectedProductData={selectedProductData}
                        storesWithStock={clickAndCollectStores}
                        selectedVariant={selectedVariant}
                        location="pdp.clickAndCollect"
                        shippingMethodKey={ShippingMethodTypes.CLICK_AND_COLLECT}
                    />
                )}
            </Box>
        );
    }
);

AccordionSingleProduct.displayName = "AccordionSingleProduct";

export default AccordionSingleProduct;
